import { useQuery } from '@tanstack/react-query'
import { CUSTOMS_STATUS_LABELS_RU, isSellerEntity, SELLER_ENTITY_LABELS_RU } from '@crm/shared'
import { getRequestActivity, type ActivityEntry } from '../../../shared/api/requests.api'

// Статусы заявки. Список — все 10 рабочих + DRAFT/ON_HOLD на исторические
// записи. Без них в истории появлялись «сырые» enum-значения вроде
// QUOTATION_CREATED.
const REQUEST_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Черновик',
  NEW: 'Новая',
  IN_PROGRESS: 'В работе',
  SENT_TO_PROCUREMENT: 'Отправлена в закупку',
  PARTIALLY_PROCESSED: 'Частично обработана',
  QUOTATION_READY: 'Готова к КП',
  QUOTATION_CREATED: 'КП выставлено',
  QUOTE_SENT: 'КП отправлено',
  CLOSED_WON: 'Счёт',
  CLOSED_LOST: 'Закрыта (отказ)',
  ARCHIVED: 'Архив',
  ON_HOLD: 'На паузе',
}

// Статусы позиции. Активные 7 + legacy 11 (старые данные ещё могут на них
// ссылаться в request_item_status_history). Без legacy раньше показывалось
// 'KP_SENT'/'REFUSED' как есть.
const ITEM_STATUS_LABELS: Record<string, string> = {
  NEW: 'Новая',
  IN_QUEUE: 'В очереди',
  IN_PROGRESS: 'В работе',
  NEEDS_INFO: 'Нужна информация',
  QUOTED: 'Проквотирована',
  PARTIAL: 'Частично готова',
  READY_FOR_QUOTATION: 'Готова к КП',
  QUOTATION_CREATED: 'КП выставлено',
  ORDERED: 'Счёт',
  REJECTED_BY_CUSTOMER: 'Отказ клиента',
  REWORK: 'На доработку',
  NOT_ACTUAL: 'Не актуальна',
  CANCELLED: 'Отменена',
  // Legacy статусы (старые данные)
  SENT_TO_PROCUREMENT: 'В закупку',
  DELIVERED: 'Доставлено',
  WORK_PROCUREMENT: 'В работе ОЗ',
  WORK_SALES: 'В работе ОП',
  KP_SENT: 'Дано КП',
  TO_PROCUREMENT: 'В закупку',
  INVOICE: 'Счёт',
  ARCHIVED: 'Архив',
  NO_OFFER: 'Нет предложения',
  REFUSED: 'Отказ клиента',
  NO_RESPONSE: 'Поставщик не ответил',
}

const FIELD_LABELS: Record<string, string> = {
  partNumber: 'Парт-номер',
  brand: 'Бренд',
  quantity: 'Кол-во',
  unit: 'Ед. изм.',
  targetPrice: 'Целевая цена',
  priceStock: 'Цена (Склад)',
  priceRF: 'Цена (РФ)',
  priceManufacturer: 'Цена (Производители)',
  priceOther: 'Цена (Стоки)',
  comment: 'Комментарий',
  priority: 'Приоритет',
  assignedToId: 'Назначен закупщик',
  noteForProcurement: 'Заметка для закупки',
  datasheetUrl: 'Даташит',
  matrix: 'Матрица',
  status: 'Статус',
  notes: 'Заметки',
  deadline: 'Дедлайн',
  // Финальные поля сделки — раньше в истории отображались сырыми именами
  // (orderedPrice, orderedFromQuotationId), Сергей просил перевод.
  orderedPrice: 'Цена в заказе',
  orderedFromQuotationId: 'КП-источник заказа',
  rejectedFromQuotationId: 'КП-источник отказа',
  rejectionReason: 'Причина отказа',
  sentAt: 'Время отправки',
  sentToProcurementAt: 'Время отправки в закупку',
  closedAt: 'Время закрытия',
  managerId: 'Менеджер',
  city: 'Город',
  sellerEntityId: 'Юрлицо продажи',
  // Нетарифка: ручная смена статуса логистом («Нужна информация», «Не
  // требуется», «Вернуть в очередь») — с 25.09.2026.
  customsStatus: 'Нетарифка',
  customsStatusNote: 'Что нужно логисту',
}

// Технические поля не отображаются в истории — они часть рабочего состояния,
// а не бизнес-событие. Например intakeMeta — объект черновика парсера
// (rawText + parsedLines), меняется на каждую правку в textarea.
// attributionUserId — техническая атрибуция KPI черновика: при смене клиента
// она следует за владельцем, а сама смена видна строкой «Менеджер».
const HIDDEN_AUDIT_FIELDS = new Set([
  'intakeMeta',
  'rawText',
  'sentToProcurementAt',
  'attributionUserId',
])

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: 'Высокий',
  MEDIUM: 'Средний',
  LOW: 'Низкий',
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (field === 'status') {
    const s = String(value)
    return ITEM_STATUS_LABELS[s] ?? REQUEST_STATUS_LABELS[s] ?? s
  }
  if (field === 'priority') return PRIORITY_LABELS[String(value)] ?? String(value)
  if (field === 'sellerEntityId' && isSellerEntity(value)) return SELLER_ENTITY_LABELS_RU[value]
  if (field === 'customsStatus') {
    const s = String(value)
    return (CUSTOMS_STATUS_LABELS_RU as Record<string, string>)[s] ?? s
  }
  return String(value)
}

type QuoteLine = {
  partNumber: string
  priceWithMarkup: number
  supplierName: string | null
  authorName: string | null
}

function describeAuditEntry(entry: ActivityEntry): string {
  const { entityType, action, before, after } = entry

  if (action === 'QUOTATION_CREATED') {
    const quotes = (after?.quotes as QuoteLine[] | undefined) ?? []
    const total = after?.totalSum as number | undefined
    if (quotes.length === 0) {
      return total != null ? `Выставлено КП на ${total} ₽` : 'Выставлено КП'
    }
    const lines = quotes.map((q) => {
      const author = q.authorName ? ` (квота ${q.authorName})` : ''
      const supplier = q.supplierName ? ` — ${q.supplierName}` : ''
      return `${q.partNumber}: ${q.priceWithMarkup} ₽${supplier}${author}`
    })
    const header =
      total != null
        ? `Выбраны квоты для КП (${quotes.length} поз., итого ${total} ₽):`
        : `Выбраны квоты для КП (${quotes.length} поз.):`
    return `${header}\n${lines.join('\n')}`
  }

  if (action === 'CREATE') {
    if (entityType === 'Request') return 'Заявка создана'
    if (entityType === 'RequestItem') {
      const pn = after?.partNumber ?? after?.partNomber ?? ''
      return pn ? `Добавлена позиция: ${pn}` : 'Добавлена позиция'
    }
    return `Создано: ${entityType}`
  }

  if (action === 'DELETE') {
    if (entityType === 'RequestItem') {
      const pn = before?.partNumber ?? before?.partNomber ?? ''
      return pn ? `Удалена позиция: ${pn}` : 'Удалена позиция'
    }
    return `Удалено: ${entityType}`
  }

  if (action === 'CUSTOMS_SENT_TO_LOGIST') {
    // Менеджер отправил позиции логисту кнопкой «Отправить брокеру» (17.10.2026).
    const parts = (after?.partNumbers as string[] | undefined) ?? []
    const count = (after?.count as number | undefined) ?? parts.length
    const list = parts.length > 0 ? ` — ${parts.join(', ')}` : ''
    return `Отправлено логисту на нетарифку: ${count} поз.${list}`
  }

  if (action === 'CUSTOMS_MISSED_MARKED' || action === 'CUSTOMS_MISSED_UNMARKED') {
    // РОП или админ отметил в отчёте «Нетарифка»: позицию нужно было отправить логисту.
    const parts = (after?.partNumbers as string[] | undefined) ?? []
    const list = parts.length > 0 ? ` — ${parts.join(', ')}` : ''
    return action === 'CUSTOMS_MISSED_MARKED'
      ? `Нетарифка: отмечено «Нужна была отправка»${list}`
      : `Нетарифка: снята отметка «Нужна была отправка»${list}`
  }

  if (action === 'CUSTOMS_ANSWER') {
    // Логист записал ответ брокера по нетарифке (Л1).
    const pn = after?.partNumber ? ` (${String(after.partNumber)})` : ''
    const flag = after?.attention ? ' — внимание' : ''
    // Логист поправил ячейку уже полученного ответа прямо в очереди.
    if (after?.corrected) return `Нетарифка: ответ изменён${pn}${flag}`
    // Логист оставил ответ прошлой проверки этого партномера, брокера заново не спрашивал.
    if (after?.source === 'PREVIOUS') {
      const at = after.previousCheckedAt
        ? ` от ${new Date(String(after.previousCheckedAt)).toLocaleDateString('ru-RU')}`
        : ''
      return `Нетарифка: оставлен прошлый ответ${at}${pn}${flag}`
    }
    return `Нетарифка: ответ получен${pn}${flag}`
  }

  if (action === 'STATUS_CHANGE') {
    // RequestItem status changes (request ones filtered on backend)
    const from = before?.status ? formatValue('status', before.status) : null
    const to = after?.status ? formatValue('status', after.status) : null
    const pn = after?.partNumber ?? before?.partNumber ?? ''
    const label = pn ? ` (${pn})` : ''
    if (from && to) return `Статус позиции${label}: ${from} → ${to}`
    if (to) return `Статус позиции${label}: ${to}`
    return `Изменён статус позиции${label}`
  }

  if (action === 'UPDATE') {
    if (!before || !after) {
      return entityType === 'Request' ? 'Заявка обновлена' : 'Позиция обновлена'
    }

    // partNumber в before/after может быть контекстом (не меняется) — используем как префикс.
    const contextPn =
      before.partNumber &&
      after.partNumber &&
      JSON.stringify(before.partNumber) === JSON.stringify(after.partNumber)
        ? String(before.partNumber)
        : null

    const changes: string[] = []
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)])
    for (const key of allKeys) {
      if (HIDDEN_AUDIT_FIELDS.has(key)) continue
      const bVal = (before as Record<string, unknown>)[key]
      const aVal = (after as Record<string, unknown>)[key]
      if (JSON.stringify(bVal) === JSON.stringify(aVal)) continue
      const fieldLabel = FIELD_LABELS[key] ?? key
      const fromStr = formatValue(key, bVal)
      const toStr = formatValue(key, aVal)
      // «— → —»: поле пустое до и после (нет ключа vs null) — не изменение.
      if (fromStr === toStr) continue
      changes.push(`${fieldLabel}: ${fromStr} → ${toStr}`)
    }

    // Пустой diff (после фильтрации HIDDEN_AUDIT_FIELDS) — запись не показываем.
    if (changes.length === 0) return ''
    const prefix = entityType === 'RequestItem' && contextPn ? `Позиция ${contextPn} · ` : ''
    return `${prefix}${changes.join('; ')}`
  }

  return `${entityType}: ${action}`
}

function formatDateTime(isoStr: string): string {
  const d = new Date(isoStr)
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function actorLabel(actor: { email: string; name?: string | null } | null): string {
  if (!actor) return 'Система'
  return actor.name?.trim() || actor.email
}

function EntryRow({ entry }: { entry: ActivityEntry }) {
  let description = ''
  let dotColor = 'bg-gray-300'

  if (entry._type === 'status') {
    // DRAFT — промежуточное рабочее состояние черновика, в истории не показываем.
    if (entry.fromStatus === 'DRAFT' || entry.toStatus === 'DRAFT') return null
    const from = entry.fromStatus
      ? (REQUEST_STATUS_LABELS[entry.fromStatus] ?? entry.fromStatus)
      : null
    const to = entry.toStatus ? (REQUEST_STATUS_LABELS[entry.toStatus] ?? entry.toStatus) : '?'
    description = from ? `Статус заявки: ${from} → ${to}` : `Статус заявки: ${to}`
    dotColor = 'bg-blue-400'
  } else {
    description = describeAuditEntry(entry)
    // Пустая запись (напр. UPDATE только по техническим полям) — скрываем.
    if (!description) return null
    if (entry.action === 'CREATE') dotColor = 'bg-green-400'
    else if (entry.action === 'DELETE') dotColor = 'bg-red-400'
    else if (entry.action === 'STATUS_CHANGE') dotColor = 'bg-blue-300'
    else if (entry.action === 'QUOTATION_CREATED') dotColor = 'bg-emerald-500'
    else dotColor = 'bg-yellow-400'
  }

  return (
    <div className="flex gap-3 text-xs">
      {/* timeline dot */}
      <div className="flex flex-col items-center pt-1 shrink-0">
        <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
      </div>
      <div className="pb-3 flex-1 min-w-0">
        <p className="text-gray-800 leading-snug whitespace-pre-line">{description}</p>
        {entry._type === 'status' && entry.note && (
          <p className="text-gray-500 italic mt-0.5">«{entry.note}»</p>
        )}
        <p className="text-gray-400 mt-0.5">
          {actorLabel(entry.actor)} · {formatDateTime(entry.createdAt)}
        </p>
      </div>
    </div>
  )
}

export default function HistoryPanel({ requestId }: { requestId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['requests', requestId, 'activity'],
    queryFn: () => getRequestActivity(requestId),
    staleTime: 30_000,
  })

  return (
    <div>
      {isLoading && <p className="text-xs text-gray-400">Загрузка истории…</p>}
      {isError && <p className="text-xs text-red-400">Не удалось загрузить историю</p>}
      {data && data.length === 0 && <p className="text-xs text-gray-400">История пуста</p>}
      {data && data.length > 0 && (
        <div className="max-h-72 overflow-y-auto pr-1">
          {data.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  )
}
